/**
 * ReportHybridSolutions — Compact hybrid solution cards linking to full solution pages.
 * Same pattern as ReportSolutions: title, snippet, score pills, "Lue lisää" link.
 */
import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, Merge, Zap, Target, Lightbulb, BookOpen } from 'lucide-react';
import { EVALUATION_TAG_META } from '@kuutio/shared/evaluationTags.js';
import { Section, SectionSubtitle, Paragraph, METRIC_STYLES } from './reportStyles';
import SolutionAnalysis, { getCompositeScore, isTagBasedEvaluation } from './SolutionAnalysis';
import SolutionImplementationPlan from './SolutionImplementationPlan';
import TranslatedText from './TranslatedText';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const HybridGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const HybridCard = styled.div`
  background: #faf8ff;
  border: 1px solid #e9e5f5;
  border-left: 4px solid #8b5cf6;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.06);
  @media print { page-break-inside: avoid; box-shadow: none; }
`;

const CardContent = styled.div`
  padding: 16px 20px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const HybridBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 0;
  color: #7c3aed;
  background: #ede9fe;
  white-space: nowrap;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
  flex: 1;
  line-height: 1.3;
`;

const TotalScore = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: ${p => p.$score >= 3 ? '#00613d' : '#fa6129'};
  white-space: nowrap;
  flex-shrink: 0;
  span { font-size: 12px; font-weight: 500; color: #9ca3af; }
`;

const SourceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const SourceChip = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 2px;
  background: #ede9fe;
  color: #5b21b6;
  font-weight: 500;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin: 10px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
`;

const ScorePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ScorePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 0;
  background: ${p => p.$bg || '#f3f4f6'};
  color: ${p => p.$color || '#6b7280'};
`;

const ReadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #8b5cf6;
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  font-family: inherit;
  &:hover { color: #6d28d9; background: rgba(139,92,246,0.08); }
  @media print { display: none; }
`;

const ExpandedDetail = styled.div`
  border-top: 1px solid #e9e5f5;
  padding: 20px;
  background: #fcfaff;
  @media print { display: none; }
`;

// Use shared METRIC_STYLES — keep icons local since they're React components.
const PILL_ICONS = { impact: Zap, feasibility: Target, innovation: Lightbulb, evidence: BookOpen };
const EVAL_PILL_STYLES = Object.fromEntries(
  Object.entries(METRIC_STYLES).map(([k, v]) => [k, { ...v, icon: PILL_ICONS[k], label: v.shortLabel }])
);

export default function ReportHybridSolutions({ hybrids = [], solutions = [], showImplementationPlans = true }) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(null);
  const MIN_HYBRID_SCORE = 3.5;
  const sortedHybrids = useMemo(() =>
    [...hybrids]
      .filter(h => {
        if (isTagBasedEvaluation(h.ai_analysis?.evaluation)) return true;
        const score = getCompositeScore(h.ai_analysis);
        return score !== null && score !== undefined && score >= MIN_HYBRID_SCORE;
      })
      .sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999)),
    [hybrids],
  );

  if (!sortedHybrids.length) return null;

  // Build lookup: solution id → display title
  const solutionTitles = {};
  solutions.forEach(s => {
    solutionTitles[s.id] = s.metadata?.ai_title || (s.title?.split(/[.!?\n]/)[0]?.substring(0, 50)) || s.title;
  });

  return (
    <Section id="hybrid-solutions" style={{ marginTop: -32, marginBottom: 64 }}>
      {/* Nested as a sub-section under Ratkaisut per käsikirjoitus 2026-05 — h3 instead of h2. */}
      <SectionSubtitle>{t('solutionAnalysis.hybridTitle')}</SectionSubtitle>
      <Paragraph>
        {t('solutionAnalysis.hybridIntro', { count: solutions.length })}{' '}
        {t('report.hybrids.selectedNote', { minScore: MIN_HYBRID_SCORE })}
      </Paragraph>

      <HybridGrid>
        {sortedHybrids.map((h, i) => {
          const compositeScore = getCompositeScore(h.ai_analysis);
          const evaluation = h.ai_analysis?.evaluation;
          const tagEval = isTagBasedEvaluation(evaluation);
          const sourceIds = h.source_solution_ids || [];
          const title = h.metadata?.ai_title || h.title;
          const snippet = h.description || h.hybrid_rationale || h.title || '';

          return (
            <HybridCard key={h.id || i}>
              <CardContent>
                <CardTop>
                  <div style={{ flex: 1 }}>
                    <HybridBadge><Merge size={10} /> {t('report.hybrids.badge')}</HybridBadge>
                    <CardTitle style={{ marginTop: 6 }}>
                      {h.id
                        ? <TranslatedText fieldKey={`hybrid.${h.id}.title`} fallback={title} multiline={false} label={t('report.hybrids.titleLabel')} />
                        : title}
                    </CardTitle>
                  </div>
                  {compositeScore !== null && (
                    <TotalScore $score={compositeScore}>
                      {compositeScore} <span>/ 5</span>
                    </TotalScore>
                  )}
                </CardTop>

                {sourceIds.length > 0 && (
                  <SourceRow>
                    {sourceIds.map(id => (
                      <SourceChip key={id}>{solutionTitles[id] || t('report.hybrids.fallbackSolution')}</SourceChip>
                    ))}
                  </SourceRow>
                )}

                <Description>
                  {h.id
                    ? <TranslatedText fieldKey={`hybrid.${h.id}.description`} fallback={snippet} label={t('report.hybrids.descriptionLabel')} />
                    : snippet}
                </Description>

                <CardFooter>
                  <ScorePills>
                    {tagEval && evaluation.tags?.map((tag) => {
                      const meta = EVALUATION_TAG_META[tag.id] || { bgLight: '#f3f4f6', color: '#374151' };
                      const labelKey = `evaluationTags.${tag.id}`;
                      const label = t(labelKey);
                      const display = label === labelKey ? tag.id : label;
                      return (
                        <ScorePill key={tag.id + (tag.reasoning || '').slice(0, 8)} $bg={meta.bgLight} $color={meta.color}>
                          {display}
                        </ScorePill>
                      );
                    })}
                    {!tagEval && evaluation && Object.entries(EVAL_PILL_STYLES).map(([key, style]) => {
                      const data = evaluation[key];
                      if (!data?.score) return null;
                      const Icon = style.icon;
                      return (
                        <ScorePill key={key} $bg={style.bg} $color={style.color}>
                          <Icon size={11} />
                          {style.label} {data.score}
                        </ScorePill>
                      );
                    })}
                  </ScorePills>
                  {h.id && (
                    <ReadMoreButton
                      type="button"
                      onClick={() => setExpandedId(expandedId === h.id ? null : h.id)}
                      aria-expanded={expandedId === h.id}
                      aria-controls={`hybrid-detail-${h.id}`}
                    >
                      {expandedId === h.id ? <>{t('report.common.close')} <ChevronUp size={14} /></> : <>{t('report.common.readMore')} <ChevronDown size={14} /></>}
                    </ReadMoreButton>
                  )}
                </CardFooter>
              </CardContent>
              {expandedId === h.id && (
                <ExpandedDetail id={`hybrid-detail-${h.id}`}>
                  <SolutionAnalysis analysis={h.ai_analysis} solutionId={h.id} />
                  {showImplementationPlans && (
                    <SolutionImplementationPlan plan={h.ai_analysis?.implementationPlan} solutionId={h.id} />
                  )}
                </ExpandedDetail>
              )}
            </HybridCard>
          );
        })}
      </HybridGrid>
    </Section>
  );
}
